import { useState, useEffect, useCallback } from "react";
import { ref, get, child } from "firebase/database";
import { db, auth } from "../../../firebase/firebase";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { getAgentUrl, DEFAULT_AGENT } from "./constants";

/**
 * Custom hook that encapsulates all chat session logic:
 * - message state, session management, save/load/clear from localStorage
 * - sending messages (handleAsk), retrying (handleReask)
 * - new chat creation
 */
export function useChatSession({
  language,
  chatLanguage,
  selectedAgent,
  setSelectedAgent,
}) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(uuidv4());
  const [isLoading, setIsLoading] = useState(false);

  // --- localStorage persistence ---

  const saveChatState = useCallback(() => {
    const user = auth.currentUser;
    if (!user) return;
    localStorage.setItem(`currentSessionId_${user.uid}`, sessionId);
    localStorage.setItem(
      `currentMessages_${user.uid}`,
      JSON.stringify(messages),
    );
  }, [sessionId, messages]);

  const clearChatState = useCallback(() => {
    const user = auth.currentUser;
    if (!user) return;
    localStorage.removeItem(`currentSessionId_${user.uid}`);
    localStorage.removeItem(`currentMessages_${user.uid}`);
  }, []);

  const loadChatState = useCallback(() => {
    const user = auth.currentUser;
    if (!user) return;

    const savedSessionId = localStorage.getItem(`currentSessionId_${user.uid}`);
    const savedMessages = localStorage.getItem(`currentMessages_${user.uid}`);

    if (savedSessionId && savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        const firstAgent = parsedMessages.find(
          (msg) => msg.role === "agent" && msg.agentType,
        );
        if (firstAgent?.agentType) {
          setSelectedAgent(firstAgent.agentType);
        }
        setSessionId(savedSessionId);
        setMessages(parsedMessages);
      } catch (error) {
        console.error("Error loading chat state:", error);
        localStorage.removeItem(`currentSessionId_${user.uid}`);
        localStorage.removeItem(`currentMessages_${user.uid}`);
      }
    }
  }, [setSelectedAgent]);

  // Load chat state on mount
  useEffect(() => {
    const user = auth.currentUser;
    if (user) loadChatState();
  }, [loadChatState]);

  // Load session from History.js (takes precedence)
  useEffect(() => {
    const sessionFromStorage = localStorage.getItem("sessionId");
    if (sessionFromStorage) {
      loadSession(sessionFromStorage);
      localStorage.removeItem("sessionId");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent sessionId from matching currentUser.uid
  useEffect(() => {
    const user = auth.currentUser;
    if (user && sessionId === user.uid) {
      setSessionId(uuidv4());
    }
  }, [sessionId]);

  // Auto-save when messages change
  useEffect(() => {
    if (messages.length > 0) saveChatState();
  }, [messages, sessionId, saveChatState]);

  // --- Load a specific session from Firebase ---

  const loadSession = async (sid) => {
    const user = auth.currentUser;
    if (!user || sid === user.uid) return;

    const snapshot = await get(
      child(ref(db), `conversations/${user.uid}/${sid}`),
    );
    if (snapshot.exists()) {
      const data = snapshot.val();
      const sorted = Object.values(data)
        .filter((v) => v && typeof v === "object" && v.role)
        .sort((a, b) => a.timestamp - b.timestamp);

      const firstAgent = sorted.find(
        (msg) => msg.role === "agent" && msg.agentType,
      );
      if (firstAgent?.agentType) {
        setSelectedAgent(firstAgent.agentType);
      }

      setMessages(sorted);
      setSessionId(sid);
    }
  };

  // --- Helpers ---

  const removeLoadingMessages = (msgs) => msgs.filter((msg) => !msg.isLoading);

  const makeApiRequest = async (message, sid, token, timeout) => {
    const postURL = getAgentUrl(selectedAgent) || getAgentUrl("chatproxy_v4");

    return axios.post(
      postURL,
      { message, sessionId: sid, language: chatLanguage },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        ...(timeout && { timeout }),
      },
    );
  };

  // --- Send a new message ---

  const handleAsk = async (input, customSessionId = null) => {
    if (!input.trim() || isLoading) return;

    setIsLoading(true);
    const user = auth.currentUser;

    if (!user) {
      setMessages((prev) => [
        ...prev,
        {
          role: "agent",
          message: "Authentication error. Please try signing in again.",
          timestamp: Date.now() + 1,
        },
      ]);
      setIsLoading(false);
      return;
    }

    const currentSessionId = customSessionId || sessionId || user.uid;

    const userMessage = {
      role: "user",
      message: input,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);

    const loadingMessage = {
      role: "agent",
      message: language === "en" ? "Finding answer..." : "尋找答案中...",
      timestamp: Date.now() + 1,
      isLoading: true,
    };
    setMessages((prev) => [...prev, loadingMessage]);

    try {
      const token = await user.getIdToken();
      const res = await makeApiRequest(input, currentSessionId, token);

      if (!res) {
        setMessages((prev) => removeLoadingMessages(prev));
        return;
      }

      console.log("API response:", res.data);

      const botMessage = {
        role: "agent",
        message: res.data.fulfillmentText || "No response received",
        timestamp: Date.now() + 1,
        id: res.data.agentMessageId || null,
        agentType: selectedAgent,
        container_actionLinks: res.data.container_actionLinks || [],
      };

      setMessages((prev) => [...removeLoadingMessages(prev), botMessage]);
    } catch (err) {
      console.error("Dialogflow request failed", err);
      let errorMessage = "Error contacting AI agent.";
      if (err.response?.status === 500) {
        errorMessage = "Authentication error. Please try signing in again.";
      } else if (err.response?.status === 400) {
        errorMessage = "Invalid request. Please try again.";
      } else if (err.response?.data?.detail) {
        errorMessage = `Service error: ${err.response.data.detail}`;
      }

      setMessages((prev) => [
        ...removeLoadingMessages(prev),
        {
          role: "agent",
          message: errorMessage,
          timestamp: Date.now() + 1,
          agentType: selectedAgent,
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Retry / regenerate a response ---

  const handleReask = async (messageToReask, targetMessageId = null) => {
    if (!messageToReask?.trim()) return;

    setIsLoading(true);
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setMessages((prev) => {
        let filtered = removeLoadingMessages(prev);

        if (targetMessageId) {
          const idx = filtered.findIndex((msg) => msg.id === targetMessageId);
          if (idx !== -1) filtered = filtered.slice(0, idx);
        } else {
          const lastAgentIdx = filtered
            .map((msg, i) => (msg.role === "agent" ? i : -1))
            .filter((i) => i !== -1)
            .pop();
          if (lastAgentIdx !== undefined && lastAgentIdx !== -1) {
            filtered = filtered.slice(0, lastAgentIdx);
          }
        }

        return [
          ...filtered,
          {
            role: "agent",
            message:
              language === "en"
                ? "Regenerating response..."
                : "重新生成回應中...",
            timestamp: Date.now(),
            isLoading: true,
            id: `loading-${Date.now()}`,
          },
        ];
      });

      const token = await user.getIdToken();
      const res = await makeApiRequest(
        messageToReask.trim(),
        sessionId,
        token,
        30000,
      );

      if (!res) {
        setMessages((prev) => removeLoadingMessages(prev));
        return;
      }

      const botMessage = {
        role: "agent",
        message:
          res.data.fulfillmentText ||
          (language === "en" ? "No response received" : "未收到回應"),
        timestamp: Date.now() + 1,
        id: res.data.agentMessageId || `agent-${Date.now()}`,
        agentType: selectedAgent,
        container_actionLinks: res.data.container_actionLinks || [],
      };

      setMessages((prev) => [...removeLoadingMessages(prev), botMessage]);
    } catch (err) {
      console.error("Reask request failed:", err);
      setMessages((prev) => [
        ...removeLoadingMessages(prev),
        {
          role: "agent",
          message:
            language === "en"
              ? "Sorry, I encountered an error while regenerating the response. Please try again."
              : "抱歉，重新生成回應時遇到錯誤。請再試一次。",
          timestamp: Date.now(),
          id: `error-${Date.now()}`,
          agentType: selectedAgent,
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // --- New chat ---

  const handleNewChat = () => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    setMessages([]);
    setSelectedAgent(DEFAULT_AGENT);
    clearChatState();
    return newSessionId;
  };

  return {
    messages,
    setMessages,
    sessionId,
    isLoading,
    handleAsk,
    handleReask,
    handleNewChat,
    loadSession,
  };
}
